import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import moment from "moment";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { DictType } from "@/model";
import { DateRange } from "@/component/dateRangePicker";


interface SentenceLineChartProps {
    statistics?: DictType<number>;
    dateRange: DateRange;
}

const SentenceLineChart: React.FC<SentenceLineChartProps> = ({ statistics, dateRange }) => {
    const intl = useIntl();

    const [dates, values] = useMemo(() => {
        if (!statistics) {
            return [[], []];
        }
        const timestamps = Object.keys(statistics).map(ts => Number(ts));
        const startDate = dateRange.start ?? moment.unix(Math.min(...timestamps)).local().startOf('day');
        const endDate = dateRange.end ?? moment.unix(Math.max(...timestamps)).local().startOf('day');
        
        const allDates: number[] = [];
        const allValues: number[] = [];
        const current = startDate.clone();
        
        while (current.isSameOrBefore(endDate, 'day')) {
            const date = current.valueOf();
            allDates.push(date);
            
            const dayValue = Object.entries(statistics)
                .filter(([timestamp]) => {
                    const statDate = moment.unix(Number(timestamp)).local().startOf('day');
                    return statDate.isSame(current, 'day');
                })
                .reduce((sum, [, value]) => sum + value, 0);
            
            allValues.push(dayValue);
            current.add(1, 'day');
        }

        return [allDates, allValues];
    }, [statistics, dateRange]);

    const ticks = useMemo(() => {
        if (dates.length <= 10) {
            return dates;
        } else if (dates.length < 50) {
            const tick = Math.ceil(dates.length / 10);
            return dates.filter((_, index) => index % tick === 0);
        } else {
            return dates.filter((_, index) => index % 5 === 0);
        }
    }, [dates]);

    if (!values.some(v => v > 0)) {
        return (
            <div className="text-black-400 text-[14px] font-[400]">
                {intl.formatMessage({ id: "i02016" })}
            </div>
        );
    }
    return (
        <Chart type="line"
               height="100%"
               className="w-full"
               series={[{
                   name: intl.formatMessage({ id: "i02019" }),
                   data: values
               }]}
               options={createChartOptions(
                   dates,
                   ticks,
                   intl.formatMessage({ id: "i02020" }),
                   intl.formatMessage({ id: "i02019" }),
               )}
        />
    );
};

const createChartOptions = (
    dates: number[], 
    ticks: number[],
    dateFormat: string,
    viewsLabel: string,
): ApexOptions => ({
    chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
    },
    colors: ["#441A98"],
    stroke: {
        width: 3,
        curve: 'straight',
    },
    markers: { size: 0 },
    dataLabels: { enabled: false },
    tooltip: {
        enabled: true,
        custom: ({ series, seriesIndex, dataPointIndex }) => {
            const date = moment(dates[dataPointIndex]).format(dateFormat);
            const value = series[seriesIndex][dataPointIndex];
            
            return (
                `<div class="w-[120px]">
                    <div class="h-7.5 flex items-center justify-center bg-chat42 text-white text-[12px] font-[400] rounded-t-[6px]">
                        ${date}
                    </div>
                    <div class="h-7.5 flex items-center justify-center bg-chat42-10 text-chat42 text-[12px] font-[400] rounded-b-[6px]">
                        ${viewsLabel}: ${value}
                    </div>
                </div>`
            );
        },
    },
    xaxis: {
        type: "datetime",
        categories: dates,
        labels: {
            datetimeUTC: false,
            formatter: (_, timestamp, opts) => {
                if (timestamp && ticks.includes(timestamp)) {
                    return opts.dateFormatter(new Date(timestamp!), "M/d");
                }
                return "";
            },
            style: {
                cssClass: "text-black-400 text-[11px] font-[400] leading-none",
                fontFamily: 'Pretendard',
            },
            rotate: 0,
        },
        tooltip: { enabled: false },
        tickAmount: dates.length - 1,
    },
    yaxis: {
        labels: {
            formatter: (value: number) => Math.trunc(value).toString(),
            minWidth: 20,
            maxWidth: 20,
            style: {
                cssClass: "text-black-400 text-[11px] font-[400] leading-none",
                fontFamily: "Pretendard",
            }
        },
        min: 0,
        forceNiceScale: true,
    }
});

export default SentenceLineChart;
